import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { supabase } from '../../lib/supabase';

const DeleteAccountModal = ({ isOpen, onClose, userEmail }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      // First, delete all user data from public schema using the safe RPC function
      const { error: dataDeleteError } = await supabase?.rpc('delete_user_data');
      
      if (dataDeleteError) {
        throw dataDeleteError;
      }

      // Then sign out and let the user know their account data has been deleted
      // Note: The auth.users record will be automatically cleaned up by Supabase
      // or can be handled by an admin function if needed
      await supabase?.auth?.signOut();
      
      // Redirect to landing page
      window.location.href = '/';
    } catch (err) {
      console.error('Delete account error:', err);
      setError(err?.message || 'Failed to delete account. Please try again or contact support.');
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} color="#ef4444" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary">Delete Account</h2>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="currentColor" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning Message */}
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} color="#ef4444" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-error mb-1">This action cannot be undone</h3>
                <p className="text-sm text-text-secondary">
                  Deleting your account will permanently remove all your data, including:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-text-secondary">
                  <li className="flex items-center space-x-2">
                    <Icon name="X" size={14} color="currentColor" />
                    <span>All training sessions and load history</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="X" size={14} color="currentColor" />
                    <span>All wellness entries and trends</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="X" size={14} color="currentColor" />
                    <span>All maturation monitoring data</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Icon name="X" size={14} color="currentColor" />
                    <span>Your profile and account settings</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Data Export Recommendation */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Download" size={20} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-accent mb-1">Export your data first</h3>
                <p className="text-sm text-text-secondary">
                  We strongly recommend exporting all your data before deleting your account. 
                  You can download reports from the Dashboard page to save your training history.
                </p>
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-muted rounded-lg p-4">
            <p className="text-sm text-text-secondary mb-1">Account to be deleted:</p>
            <p className="text-sm font-medium text-text-primary">{userEmail}</p>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Type <span className="font-bold text-error">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => {
                setConfirmText(e?.target?.value);
                setError(null);
              }}
              placeholder="Type DELETE here"
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-error focus:border-transparent"
              disabled={isDeleting}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-3">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting || confirmText !== 'DELETE'}
            className="bg-error hover:bg-error/90 text-white"
          >
            {isDeleting ? (
              <>
                <Icon name="Loader2" size={16} color="currentColor" className="animate-spin mr-2" />
                Deleting...
              </>
            ) : (
              <>
                <Icon name="Trash2" size={16} color="currentColor" className="mr-2" />
                Delete Account Permanently
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;